/**
 * « Quand commander le pain ? » — la ligne du temps de la précommande, en dessin.
 *
 * La question que les clients posent le plus (Philippe, 27/09/2026). Première
 * version en tableau de jours : juste, mais il fallait la lire. « Les gens ont
 * du mal à lire, un dessin est plus vite compris » (Philippe, 28/09) → une bande
 * de trois cases par jour de cuisson : l'horloge de l'heure limite, la pâte qui
 * lève 16 h, le sac à pain qu'on emporte. Presque plus de phrases.
 *
 * Toujours dessinée depuis les réglages du type de précommande (servis par
 * /shop-config) : si le boulanger change ses jours ou son heure limite, le
 * dessin suit. Une image fixe (infographie) serait fausse au premier changement,
 * et illisible sur un téléphone tenu en hauteur.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { fetchShopConfigCached } from '../../hooks/ecommerce/useShopOrdering';
import {
  ligneTempsPrecommande,
  heureTexte,
  NOMS_JOURS,
  type TypePrecommande,
} from '../../utils/ligneTempsPrecommande';

const OLIVE = '#4a5c3a';
const TERRACOTTA = '#b45f2e';
const CREME = '#f6efe2';
const PATE = '#ecd6a6';
const CROUTE = '#c8893f';

/* ---------- Dessins ---------- */

/** Réveil arrêté sur l'heure limite : le chiffre de l'heure est écrit sur le
 *  cadran (à 6 h les deux aiguilles s'alignent et ne se lisent plus seules). */
function Horloge({ heure, taille }: { heure: number; taille: number }) {
  const h12 = heure % 12;
  const a = (h12 / 12) * 2 * Math.PI;
  const x = 24 + Math.sin(a) * 5.5;
  const y = 26 - Math.cos(a) * 5.5;
  const tx = 24 + Math.sin(a) * 11.5;
  const ty = 26 - Math.cos(a) * 11.5;
  return (
    <svg width={taille} height={taille} viewBox="0 0 48 48" aria-hidden>
      <path d="M9 12 L14 7 M39 12 L34 7" stroke={OLIVE} strokeWidth="3" strokeLinecap="round" />
      <circle cx="24" cy="26" r="17" fill={CREME} stroke={OLIVE} strokeWidth="2.5" />
      {[0, 3, 6, 9]
        .filter((t) => t !== h12)
        .map((t) => {
          const b = (t / 12) * 2 * Math.PI;
          return (
            <line
              key={t}
              x1={24 + Math.sin(b) * 13.5}
              y1={26 - Math.cos(b) * 13.5}
              x2={24 + Math.sin(b) * 16}
              y2={26 - Math.cos(b) * 16}
              stroke={OLIVE}
              strokeWidth="2"
              strokeLinecap="round"
            />
          );
        })}
      <text x={tx} y={ty} textAnchor="middle" dominantBaseline="central" fontSize="9.5" fontWeight="800" fill={TERRACOTTA} fontFamily="system-ui, sans-serif">
        {heure === 0 ? '12' : h12 === 0 ? '12' : h12}
      </text>
      <line x1="24" y1="26" x2="24" y2="14" stroke={OLIVE} strokeWidth="1.8" strokeLinecap="round" />
      <line x1="24" y1="26" x2={x} y2={y} stroke={TERRACOTTA} strokeWidth="3.2" strokeLinecap="round" />
      <circle cx="24" cy="26" r="2" fill={TERRACOTTA} />
    </svg>
  );
}

/** Bol de pâte qui gonfle (animé, sauf si l'appareil demande moins de mouvement). */
function PateQuiLeve({ taille }: { taille: number }) {
  return (
    <svg width={taille} height={taille} viewBox="0 0 48 48" aria-hidden>
      <g className="ldt-leve" style={{ transformOrigin: '24px 30px' }}>
        <path d="M8 30 C8 13 40 13 40 30 Z" fill={PATE} stroke={CROUTE} strokeWidth="2" />
        <circle cx="18" cy="23" r="1.3" fill={CROUTE} opacity=".6" />
        <circle cx="27" cy="20" r="1.1" fill={CROUTE} opacity=".6" />
        <circle cx="31" cy="25" r="1.2" fill={CROUTE} opacity=".6" />
      </g>
      <path d="M5 30 H43 C43 40 35 45 24 45 C13 45 5 40 5 30 Z" fill={OLIVE} />
      <path d="M24 10 V3 M20.5 6.5 L24 3 L27.5 6.5" stroke={TERRACOTTA} strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Sac à pain d'où dépasse une miche : on vient le chercher. */
function SacAPain({ taille }: { taille: number }) {
  return (
    <svg width={taille} height={taille} viewBox="0 0 48 48" aria-hidden>
      <path d="M17 21 C17 13 31 13 31 21" stroke={OLIVE} strokeWidth="2.5" fill="none" />
      <g transform="rotate(18 25 16)">
        <rect x="18.5" y="1" width="12" height="30" rx="6" fill={CROUTE} />
        <path d="M21 7 L28 5 M21 13 L28 11 M21 19 L28 17" stroke={PATE} strokeWidth="1.9" strokeLinecap="round" />
      </g>
      <path d="M10 21 H38 L35 44 H13 Z" fill={CREME} stroke={OLIVE} strokeWidth="2.5" strokeLinejoin="round" />
      <path d="M17 30 H31" stroke={OLIVE} strokeWidth="2" strokeLinecap="round" opacity=".5" />
    </svg>
  );
}

function Fleche() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden className="shrink-0 self-center mb-6">
      <path d="M4 12 H18 M13 6 L19 12 L13 18" stroke={OLIVE} strokeWidth="2.6" fill="none" strokeLinecap="round" strokeLinejoin="round" opacity=".55" />
    </svg>
  );
}

function Case({ dessin, grand, petit, accent }: { dessin: ReactNode; grand: string; petit: string; accent: string }) {
  return (
    <div className="flex-1 min-w-0 flex flex-col items-center text-center">
      <div className="rounded-2xl w-full flex justify-center py-2" style={{ background: `${accent}12` }}>
        {dessin}
      </div>
      <div className="mt-1.5 font-extrabold text-[15px] leading-tight" style={{ color: accent }}>
        {grand}
      </div>
      <div className="text-[11.5px] leading-tight text-[#5a5440]">{petit}</div>
    </div>
  );
}

/* ---------- Petits pictos des rappels ---------- */

function PictoTropTard() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden className="shrink-0">
      <circle cx="10" cy="12" r="7" fill="none" stroke={TERRACOTTA} strokeWidth="2" />
      <path d="M10 8 V12 L12.5 13.5" stroke={TERRACOTTA} strokeWidth="2" fill="none" strokeLinecap="round" />
      <path d="M18 9 L22 12 L18 15" stroke={OLIVE} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function PictoCalendrier() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden className="shrink-0">
      <rect x="3" y="5" width="18" height="16" rx="2.5" fill={CREME} stroke={OLIVE} strokeWidth="2" />
      <path d="M3 10 H21 M8 3 V7 M16 3 V7" stroke={OLIVE} strokeWidth="2" strokeLinecap="round" />
      <circle cx="15.5" cy="15" r="2.3" fill={TERRACOTTA} />
    </svg>
  );
}
/** « du samedi 24 octobre au lundi 2 novembre » (dates AAAA-MM-JJ, lues à midi
 *  pour ne jamais glisser d'un jour selon le fuseau). */
function periodeTexte(debut: string, fin: string): string {
  const f = (d: string) =>
    new Date(`${d}T12:00:00`).toLocaleDateString('fr-BE', { weekday: 'long', day: 'numeric', month: 'long' });
  return debut === fin ? `Le ${f(debut)}` : `Du ${f(debut)} au ${f(fin)}`;
}

function PictoConge({ taille = 22 }: { taille?: number }) {
  return (
    <svg width={taille} height={taille} viewBox="0 0 24 24" aria-hidden className="shrink-0">
      <path d="M8 7 L12 3 L16 7" stroke={OLIVE} strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="3" y="7" width="18" height="12" rx="2" fill={CREME} stroke={OLIVE} strokeWidth="2" />
      <path d="M8.5 10 L15.5 16 M15.5 10 L8.5 16" stroke={TERRACOTTA} strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

/* ---------- Composant ---------- */

export default function LigneDuTempsPain({ slug = 'pain', compact = false }: { slug?: string; compact?: boolean }) {
  const [type, setType] = useState<TypePrecommande | null>(null);
  const [conges, setConges] = useState<Array<{ start_date: string; end_date: string }>>([]);

  useEffect(() => {
    let annule = false;
    fetchShopConfigCached()
      .then((c) => {
        if (annule) return;
        setType((c.preorders || []).find((t) => t.slug === slug) ?? null);
        setConges(slug === 'pain' ? c.baker_closures || [] : []);
      })
      .catch(() => {});
    return () => {
      annule = true;
    };
  }, [slug]);

  const ligne = ligneTempsPrecommande(type);
  if (!type || !ligne) return null;

  const t = compact ? 44 : 52;

  return (
    <div className={`shop-sans rounded-2xl border border-[#4a5c3a]/20 bg-white ${compact ? 'p-3' : 'p-4 sm:p-5'}`}>
      <style>{`
        @keyframes ldt-leve { 0%, 15% { transform: scale(.8, .55); } 70%, 100% { transform: scale(1, 1); } }
        .ldt-leve { animation: ldt-leve 3.2s ease-in-out infinite alternate; }
        @media (prefers-reduced-motion: reduce) { .ldt-leve { animation: none; } }
      `}</style>

      {!compact && (
        <h3 className="font-bold text-[16px] mb-1" style={{ color: TERRACOTTA }}>
          Le pain se commande à l'avance
        </h3>
      )}
      <p className="text-[13px] text-[#4a4530] mb-3">
        Pains au levain : <strong>16 h</strong> de pousse.
      </p>

      {conges.map((c) => (
        <div
          key={c.start_date}
          className="mb-3 flex items-center gap-3 rounded-xl border-2 px-3 py-2.5"
          style={{ borderColor: `${TERRACOTTA}55`, background: `${TERRACOTTA}0d` }}
        >
          <PictoConge taille={30} />
          <span className="text-[13.5px] leading-snug text-[#3a3324]">
            <strong style={{ color: TERRACOTTA }}>Congé du boulanger</strong>
            <br />
            {periodeTexte(c.start_date, c.end_date)} : pas de pain ces jours-là.
          </span>
        </div>
      ))}

      <div className="space-y-4">
        {ligne.etapes.map((e, i) => {
          const retrait = e.joursRetrait.map((r) => NOMS_JOURS[r]).join(' / ');
          return (
            <div key={i}>
              <div className="text-[12.5px] font-bold uppercase tracking-[.06em] mb-1.5" style={{ color: OLIVE }}>
                {e.jourFabrication != null ? `Pain du ${NOMS_JOURS[e.jourFabrication]}` : type.name}
              </div>
              <div className="flex items-start gap-1.5">
                <Case
                  dessin={<Horloge heure={e.heureLimite} taille={t} />}
                  grand={NOMS_JOURS[e.jourLimite]}
                  petit={`commandez avant ${heureTexte(e.heureLimite)}`}
                  accent={TERRACOTTA}
                />
                <Fleche />
                {e.jourFabrication != null && (
                  <>
                    <Case dessin={<PateQuiLeve taille={t} />} grand="16 h" petit="la pâte lève" accent={CROUTE} />
                    <Fleche />
                  </>
                )}
                <Case dessin={<SacAPain taille={t} />} grand={retrait} petit="vous le retirez" accent={OLIVE} />
              </div>
              <p className="sr-only">
                Commander au plus tard le {NOMS_JOURS[e.jourLimite]} à {heureTexte(e.heureLimite)}, retrait le{' '}
                {e.joursRetrait.map((r) => NOMS_JOURS[r]).join(' ou le ')}.
              </p>
            </div>
          );
        })}
      </div>

      <ul className="mt-4 pt-3 border-t border-[#4a5c3a]/10 space-y-2 text-[12.5px] text-[#4a4530]">
        <li className="flex items-center gap-2">
          <PictoTropTard />
          <span>Trop tard ? Le pain part au jour de cuisson suivant.</span>
        </li>
        <li className="flex items-center gap-2">
          <PictoCalendrier />
          <span>Certains pains ne cuisent qu'un jour : voir leur fiche.</span>
        </li>
      </ul>
    </div>
  );
}
